/**
 * Template Diff Viewer Component
 *
 * FERP CRITICAL: Unified diff display for template changes
 * Shows before/after changes with syntax highlighting and line numbers
 *
 * Principles:
 * - Visual clarity: Syntax highlighting for JSON diffs
 * - Complete transparency: Show all changes before applying
 * - Audit trail: Diff stored in DynamoDB for compliance
 * - Human review: No automatic application without explicit approval
 *
 * Usage:
 * ```tsx
 * <TemplateDiffViewer
 *   diff={recommendation.diff}
 *   templateName="Healthcare RCM Template"
 * />
 * ```
 */

import React from 'react';
import {
  Paper,
  Text,
  Stack,
  Box,
  ScrollArea,
  Group,
  Badge,
  Code,
  Divider,
} from '@mantine/core';
import {
  IconFileCode,
  IconPlus,
  IconMinus,
  IconEqual,
} from '@tabler/icons-react';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export interface TemplateDiffViewerProps {
  /** Unified diff string from backend (git-style) */
  diff: string;

  /** Template name for display */
  templateName?: string;

  /** Maximum height for scrollable area */
  maxHeight?: number | string;

  /** Show line numbers */
  showLineNumbers?: boolean;

  /** Show stats badge (additions/deletions) */
  showStats?: boolean;
}

// ============================================================================
// DIFF PARSING TYPES
// ============================================================================

interface DiffLine {
  type: 'addition' | 'deletion' | 'context' | 'header';
  content: string;
  oldLineNumber?: number;
  newLineNumber?: number;
}

interface DiffStats {
  additions: number;
  deletions: number;
  totalLines: number;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse unified diff into structured lines
 */
function parseDiff(diffString: string): DiffLine[] {
  const lines = diffString.split('\n');
  const parsed: DiffLine[] = [];
  let oldLineNumber = 0;
  let newLineNumber = 0;

  for (const line of lines) {
    if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('@@')) {
      // Header lines (file markers and hunk headers)
      parsed.push({
        type: 'header',
        content: line,
      });

      // Parse hunk header to get line numbers
      if (line.startsWith('@@')) {
        const match = line.match(/@@ -(\d+),?\d* \+(\d+),?\d* @@/);
        if (match) {
          oldLineNumber = parseInt(match[1], 10) - 1;
          newLineNumber = parseInt(match[2], 10) - 1;
        }
      }
    } else if (line.startsWith('+')) {
      // Addition
      newLineNumber++;
      parsed.push({
        type: 'addition',
        content: line.substring(1),
        newLineNumber,
      });
    } else if (line.startsWith('-')) {
      // Deletion
      oldLineNumber++;
      parsed.push({
        type: 'deletion',
        content: line.substring(1),
        oldLineNumber,
      });
    } else if (line.startsWith(' ')) {
      // Context line (unchanged)
      oldLineNumber++;
      newLineNumber++;
      parsed.push({
        type: 'context',
        content: line.substring(1),
        oldLineNumber,
        newLineNumber,
      });
    }
  }

  return parsed;
}

/**
 * Calculate diff statistics
 */
function calculateStats(lines: DiffLine[]): DiffStats {
  return {
    additions: lines.filter((l) => l.type === 'addition').length,
    deletions: lines.filter((l) => l.type === 'deletion').length,
    totalLines: lines.filter((l) => l.type !== 'header').length,
  };
}

/**
 * Get background color for diff line type
 */
function getLineBackgroundColor(type: DiffLine['type']): string {
  switch (type) {
    case 'addition':
      return 'var(--mantine-color-green-0)';
    case 'deletion':
      return 'var(--mantine-color-red-0)';
    case 'header':
      return 'var(--mantine-color-gray-1)';
    case 'context':
    default:
      return 'transparent';
  }
}

/**
 * Get line icon
 */
function getLineIcon(type: DiffLine['type']): React.ReactNode {
  switch (type) {
    case 'addition':
      return <IconPlus size={12} color="var(--mantine-color-green-6)" />;
    case 'deletion':
      return <IconMinus size={12} color="var(--mantine-color-red-6)" />;
    case 'context':
      return <IconEqual size={12} color="var(--mantine-color-gray-5)" />;
    default:
      return null;
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const TemplateDiffViewer: React.FC<TemplateDiffViewerProps> = ({
  diff,
  templateName,
  maxHeight = 400,
  showLineNumbers = true,
  showStats = true,
}) => {
  const diffLines = parseDiff(diff);
  const stats = calculateStats(diffLines);

  return (
    <Paper shadow="xs" padding="md" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <IconFileCode size={20} />
            <Box>
              <Text size="sm" fw={600}>
                Template Changes
              </Text>
              {templateName && (
                <Text size="xs" c="dimmed">
                  {templateName}
                </Text>
              )}
            </Box>
          </Group>

          {/* Stats badges */}
          {showStats && (
            <Group gap="xs">
              <Badge color="green" variant="light" size="sm">
                +{stats.additions}
              </Badge>
              <Badge color="red" variant="light" size="sm">
                -{stats.deletions}
              </Badge>
              <Badge color="gray" variant="light" size="sm">
                {stats.totalLines} lines
              </Badge>
            </Group>
          )}
        </Group>

        <Divider />

        {/* Diff display */}
        <ScrollArea h={maxHeight} type="auto">
          <Code
            block
            style={{
              backgroundColor: 'var(--mantine-color-gray-0)',
              padding: 0,
              margin: 0,
            }}
          >
            {diffLines.map((line, index) => (
              <Box
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  backgroundColor: getLineBackgroundColor(line.type),
                  padding: '2px 8px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  lineHeight: '1.6',
                  borderLeft:
                    line.type === 'addition'
                      ? '3px solid var(--mantine-color-green-6)'
                      : line.type === 'deletion'
                      ? '3px solid var(--mantine-color-red-6)'
                      : 'none',
                }}
              >
                {/* Line icon */}
                <Box
                  style={{
                    width: '16px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '8px',
                  }}
                >
                  {getLineIcon(line.type)}
                </Box>

                {/* Line numbers */}
                {showLineNumbers && line.type !== 'header' && (
                  <>
                    <Text
                      span
                      size="xs"
                      c="dimmed"
                      style={{
                        width: '40px',
                        textAlign: 'right',
                        marginRight: '8px',
                        userSelect: 'none',
                      }}
                    >
                      {line.oldLineNumber || ''}
                    </Text>
                    <Text
                      span
                      size="xs"
                      c="dimmed"
                      style={{
                        width: '40px',
                        textAlign: 'right',
                        marginRight: '8px',
                        userSelect: 'none',
                      }}
                    >
                      {line.newLineNumber || ''}
                    </Text>
                  </>
                )}

                {/* Line content */}
                <Text
                  span
                  size="xs"
                  style={{
                    flex: 1,
                    whiteSpace: 'pre',
                    fontFamily: 'monospace',
                    color:
                      line.type === 'header'
                        ? 'var(--mantine-color-gray-7)'
                        : line.type === 'addition'
                        ? 'var(--mantine-color-green-9)'
                        : line.type === 'deletion'
                        ? 'var(--mantine-color-red-9)'
                        : 'inherit',
                    fontWeight: line.type === 'header' ? 600 : 400,
                  }}
                >
                  {line.content}
                </Text>
              </Box>
            ))}
          </Code>
        </ScrollArea>

        {/* Footer info */}
        <Text size="xs" c="dimmed">
          Review changes carefully before applying. All modifications are tracked for audit compliance.
        </Text>
      </Stack>
    </Paper>
  );
};

export default TemplateDiffViewer;
